import React, { useState, useEffect } from 'react';
import { Calendar, TrendingUp, DollarSign, Clock, CheckCircle, AlertTriangle, Search, Download, Plus, Mail, Phone, MessageSquare, Star, FileText, User, Users, MapPin, X, Edit } from 'lucide-react';
import ReservationDetail from './ReservationDetail';

const ReservationsCenter = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState('month');
  const [showReservationDetail, setShowReservationDetail] = useState(false);
  const [selectedReservationId, setSelectedReservationId] = useState(null);

  // Statistiques du pipeline
  const pipelineStats = {
    totalPipeline: "€4.2M",
    activeReservations: 36,
    confirmedThisMonth: 12,
    confirmedRevenue: "€1.8M",
    actionsRequired: 8,
    actionsBreakdown: { quotes: 5, contracts: 3 },
    conversionRate: 68,
    conversionGrowth: 12
  };

  // Étapes du workflow
  const workflowSteps = [
    { id: 'demande', label: 'Demande', icon: '📧', color: 'blue' },
    { id: 'qualification', label: 'Qualification', icon: '💬', color: 'indigo' },
    { id: 'proposition', label: 'Proposition', icon: '📋', color: 'yellow' },
    { id: 'contrat', label: 'Contrat', icon: '✍️', color: 'orange' },
    { id: 'acompte', label: 'Acompte', icon: '💳', color: 'purple' },
    { id: 'confirmation', label: 'Confirmation', icon: '✅', color: 'green' },
    { id: 'checkin', label: 'Check-in', icon: '🏡', color: 'teal' },
    { id: 'complete', label: 'Complété', icon: '⭐', color: 'gray' }
  ];

  // Répartition du pipeline
  const pipelineBreakdown = [
    { stage: 'all', count: 36, label: 'Toutes' },
    { stage: 'demandes', count: 8, label: 'Demandes' },
    { stage: 'devis', count: 5, label: 'Devis' },
    { stage: 'attente', count: 7, label: 'En Attente' },
    { stage: 'confirmees', count: 12, label: 'Confirmées' },
    { stage: 'encours', count: 4, label: 'En Cours' }
  ];

  // Réservations actives
  const activeReservations = [
    {
      id: 'PRV2591',
      clientInitials: 'NB',
      clientName: 'Nicolas Beaumont',
      clientType: 'Nouveau Client',
      property: 'Villa Celestial',
      location: 'Mykonos',
      dates: '20-27 Juil 2025',
      amount: 175000,
      status: 'demande',
      priority: 'new',
      lastActivity: '2024-09-14 10:23'
    },
    {
      id: 'PRV2590',
      clientInitials: 'JD',
      clientName: 'James Davidson',
      clientType: 'Diamond Elite',
      property: 'Villa Azure Dreams',
      location: 'St-Barthélemy',
      dates: '15-29 Déc 2025',
      amount: 420000,
      status: 'devis',
      priority: 'diamond',
      lastActivity: '2024-09-13 16:45'
    },
    {
      id: 'PRV2589',
      clientInitials: 'SC',
      clientName: 'Sophie Chen',
      clientType: 'Platinum',
      property: 'M/Y Wind Spirit',
      location: 'Côte d\'Azur',
      dates: '10-17 Oct 2025',
      amount: 320000,
      status: 'attente-paiement',
      priority: 'platinum',
      lastActivity: '2024-09-12 14:20'
    },
    {
      id: 'PRV2588',
      clientInitials: 'MA',
      clientName: 'Mohammed Al-Rashid',
      clientType: 'Diamond Elite',
      property: 'Villa Paradiso',
      location: 'St-Tropez',
      dates: '12-26 Sep 2025',
      amount: 630000,
      status: 'confirmee',
      priority: 'diamond',
      lastActivity: '2024-09-11 09:15'
    },
    {
      id: 'PRV2587',
      clientInitials: 'EW',
      clientName: 'Emma Williams',
      clientType: 'Gold',
      property: 'Villa Sunset',
      location: 'Sardaigne',
      dates: '8-15 Sep 2025',
      amount: 95000,
      status: 'en-sejour',
      priority: 'gold',
      lastActivity: '2024-09-08 11:30'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'demande': return 'bg-blue-100 text-blue-800';
      case 'devis': return 'bg-yellow-100 text-yellow-800';
      case 'attente-paiement': return 'bg-orange-100 text-orange-800';
      case 'confirmee': return 'bg-green-100 text-green-800';
      case 'en-sejour': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'demande': return '📧';
      case 'devis': return '📋';
      case 'attente-paiement': return '💳';
      case 'confirmee': return '✅';
      case 'en-sejour': return '🏡';
      default: return '📄';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'demande': return 'Demande';
      case 'devis': return 'Devis Envoyé';
      case 'attente-paiement': return 'Attente Paiement';
      case 'confirmee': return 'Confirmée';
      case 'en-sejour': return 'En Séjour';
      default: return status;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'diamond': return 'bg-purple-100 text-purple-800';
      case 'platinum': return 'bg-gray-100 text-gray-800';
      case 'gold': return 'bg-yellow-100 text-yellow-800';
      case 'new': return 'bg-green-100 text-green-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const filteredReservations = activeReservations.filter(reservation => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'demandes') return reservation.status === 'demande';
    if (activeFilter === 'devis') return reservation.status === 'devis';
    if (activeFilter === 'attente') return reservation.status === 'attente-paiement';
    if (activeFilter === 'confirmees') return reservation.status === 'confirmee';
    if (activeFilter === 'encours') return reservation.status === 'en-sejour';
    return true;
  });

  const ReservationDetailsModal = () => {
    if (!showDetailsModal || !selectedReservation) return null;

    const reservation = activeReservations.find(r => r.id === selectedReservation);
    if (!reservation) return null;

    // Données détaillées de la réservation (exemple pour James Davidson)
    const detailsData = {
      progression: [
        { step: 'Demande Reçue', date: '8 Sep 2025, 14:23', completed: true },
        { step: 'Client Qualifié', date: '8 Sep 2025, 16:45', completed: true },
        { step: 'Devis Envoyé', date: '9 Sep 2025, 10:15', completed: true, current: true },
        { step: 'Contrat', date: null, completed: false },
        { step: 'Acompte 30%', date: null, completed: false }
      ],
      details: {
        property: 'Villa Azure Dreams',
        destination: 'St-Barthélemy, Gustavia',
        checkin: '15 Décembre 2025',
        checkout: '29 Décembre 2025',
        duration: '14 nuits',
        guests: '8 adultes, 2 enfants'
      },
      financial: {
        villaRental: 378000,
        conciergeServices: 42000,
        touristTax: 21000,
        total: 441000
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center p-6 border-b border-gray-200">
            <div>
              <h3 className="text-xl font-semibold text-gray-800">Réservation #{reservation.id}</h3>
              <p className="text-sm text-gray-600">{reservation.clientName} • {reservation.property}</p>
            </div>
            <button
              onClick={() => setShowDetailsModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Progression */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-4">📈 Progression</h4>
              <div className="space-y-3">
                {detailsData.progression.map((step, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded-full ${
                      step.completed ? 'bg-green-500' : step.current ? 'bg-blue-500' : 'bg-gray-300'
                    }`}></div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <span className={`font-medium ${step.current ? 'text-blue-600' : 'text-gray-800'}`}>
                          {step.step}
                        </span>
                        {step.date && (
                          <span className="text-sm text-gray-500">{step.date}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Détails Réservation */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-4">📋 Détails Réservation</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Propriété</span>
                    <span className="font-medium">{detailsData.details.property}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Destination</span>
                    <span className="font-medium">{detailsData.details.destination}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Check-in</span>
                    <span className="font-medium">{detailsData.details.checkin}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Check-out</span>
                    <span className="font-medium">{detailsData.details.checkout}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Durée</span>
                    <span className="font-medium">{detailsData.details.duration}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Invités</span>
                    <span className="font-medium">{detailsData.details.guests}</span>
                  </div>
                </div>
              </div>

              {/* Résumé Financier */}
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-4">💰 Résumé Financier</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Location Villa (14 nuits)</span>
                    <span className="font-medium">€{detailsData.financial.villaRental.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Services Conciergerie</span>
                    <span className="font-medium">€{detailsData.financial.conciergeServices.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Taxe de séjour (5%)</span>
                    <span className="font-medium">€{detailsData.financial.touristTax.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 font-bold">
                    <span className="text-gray-800">Total</span>
                    <span className="text-green-600">€{detailsData.financial.total.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions Rapides */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-4">⚡ Actions Rapides</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <button className="flex items-center space-x-2 px-3 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
                  <FileText size={16} />
                  <span>Générer Contrat</span>
                </button>
                <button className="flex items-center space-x-2 px-3 py-2 text-green-600 border border-green-300 rounded-lg hover:bg-green-50">
                  <DollarSign size={16} />
                  <span>Demander Acompte</span>
                </button>
                <button className="flex items-center space-x-2 px-3 py-2 text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50">
                  <Mail size={16} />
                  <span>Envoyer Rappel</span>
                </button>
                <button className="flex items-center space-x-2 px-3 py-2 text-orange-600 border border-orange-300 rounded-lg hover:bg-orange-50">
                  <Phone size={16} />
                  <span>Appeler Client</span>
                </button>
                <button className="flex items-center space-x-2 px-3 py-2 text-yellow-600 border border-yellow-300 rounded-lg hover:bg-yellow-50">
                  <Star size={16} />
                  <span>Ajouter Services</span>
                </button>
                <button className="flex items-center space-x-2 px-3 py-2 text-teal-600 border border-teal-300 rounded-lg hover:bg-teal-50">
                  <FileText size={16} />
                  <span>Documents</span>
                </button>
                <button className="flex items-center space-x-2 px-3 py-2 text-indigo-600 border border-indigo-300 rounded-lg hover:bg-indigo-50">
                  <Edit size={16} />
                  <span>Modifier Devis</span>
                </button>
                <button className="flex items-center space-x-2 px-3 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50">
                  <X size={16} />
                  <span>Annuler</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Si on est sur la page de détail, afficher uniquement le composant ReservationDetail
  if (showReservationDetail) {
    return (
      <ReservationDetail 
        reservationId={selectedReservationId}
        onBack={() => {
          setShowReservationDetail(false);
          setSelectedReservationId(null);
        }}
      />
    );
  }

  return (
    <div className="reservations-center">
      {/* En-tête */}
      <div className="page-header bg-white border-b border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Centre de Réservations</h1>
            <p className="text-gray-600 mt-1">Workflow de Réservation PRIVIA - Gestion complète du pipeline</p>
          </div>
          
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <button className="flex items-center space-x-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              <Search size={18} />
              <span>Rechercher</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
              <Download size={18} />
              <span>Export</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
              <Plus size={18} />
              <span>Nouvelle Réservation</span>
            </button>
          </div>
        </div>

        {/* Workflow visuel */}
        <div className="mt-6 bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">📊 Workflow de Réservation PRIVIA</h3>
          <div className="flex items-center justify-between">
            {workflowSteps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-white border-2 border-gray-300 rounded-full flex items-center justify-center text-lg">
                    {step.icon}
                  </div>
                  <span className="text-xs text-gray-600 mt-1 text-center max-w-20">{step.label}</span>
                </div>
                {index < workflowSteps.length - 1 && (
                  <div className="w-8 h-0.5 bg-gray-300 mx-2"></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="text-blue-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{pipelineStats.totalPipeline}</div>
            <div className="text-sm text-gray-600">Pipeline Total</div>
            <div className="text-xs text-gray-500">{pipelineStats.activeReservations} réservations actives</div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="text-green-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{pipelineStats.confirmedThisMonth}</div>
            <div className="text-sm text-gray-600">Confirmées ce Mois</div>
            <div className="text-xs text-gray-500">{pipelineStats.confirmedRevenue} de revenue</div>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Clock className="text-orange-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{pipelineStats.actionsRequired}</div>
            <div className="text-sm text-gray-600">Actions Requises</div>
            <div className="text-xs text-gray-500">{pipelineStats.actionsBreakdown.quotes} devis, {pipelineStats.actionsBreakdown.contracts} contrats</div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="text-purple-600" size={24} />
              <span className="text-sm font-medium text-purple-600">+{pipelineStats.conversionGrowth}%</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{pipelineStats.conversionRate}%</div>
            <div className="text-sm text-gray-600">Taux Conversion</div>
            <div className="text-xs text-gray-500">vs mois dernier</div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Filtres */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800">Filtrer les réservations</h3>
            <div className="flex space-x-2">
              <select
                value={selectedTimeframe}
                onChange={(e) => setSelectedTimeframe(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              >
                <option value="week">Cette Semaine</option>
                <option value="month">Ce Mois</option>
                <option value="all">Toutes</option>
              </select>
              <button className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
                Filtres Avancés
              </button>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {pipelineBreakdown.map((item) => (
              <button
                key={item.stage}
                onClick={() => setActiveFilter(item.stage)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
                  activeFilter === item.stage
                    ? 'bg-yellow-50 border-yellow-300 text-yellow-800'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span>{item.label}</span>
                <span className="bg-gray-200 text-gray-700 text-xs px-2 py-0.5 rounded-full">
                  {item.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Tableau des réservations */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">📋 Réservations Actives</h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Propriété
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dates
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredReservations.map((reservation) => (
                  <tr key={reservation.id} className="hover:bg-gray-50">
                    <td 
                      className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600 hover:text-blue-700 cursor-pointer"
                      onClick={() => {
                        setSelectedReservationId(reservation.id);
                        setShowReservationDetail(true);
                      }}
                    >
                      #{reservation.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                          {reservation.clientInitials}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{reservation.clientName}</div>
                          <div className={`text-xs px-2 py-0.5 rounded-full inline-flex ${getPriorityColor(reservation.priority)}`}>
                            {reservation.clientType}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{reservation.property}</div>
                      <div className="text-sm text-gray-500">{reservation.location}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {reservation.dates}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      €{reservation.amount.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(reservation.status)}`}>
                        <span className="mr-1">{getStatusIcon(reservation.status)}</span>
                        {getStatusText(reservation.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedReservation(reservation.id);
                            setShowDetailsModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          ✔️
                        </button>
                        <button className="text-yellow-600 hover:text-yellow-900">
                          📋
                        </button>
                        <button className="text-green-600 hover:text-green-900">
                          💬
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {!showReservationDetail && <ReservationDetailsModal />}
    </div>
  );
};

export default ReservationsCenter;